import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Building2, Phone, Mail, User, MapPin } from "lucide-react";

const OFFICES = [
  {
    key: "in-1",
    flag: "🇮🇳",
    label: "India Office · 1",
    legalName: "APEX Innovation Solutions",
    idLabel: "Registration No.",
    idValue: "SCG126021000459541",
    lines: [
      "2nd Floor, 113, Shri Kuber Complex,",
      "Near Indo Canadian Bus Services,",
      "Jalandhar, Punjab 144001, India",
    ],
    tint: "glass-tint-cyan",
    accentDot: "bg-apex-cyan",
  },
  {
    key: "in-2",
    flag: "🇮🇳",
    label: "India Office · 2",
    legalName: "APEX Innovation Solutions",
    idLabel: "Registration No.",
    idValue: "SCG126021000459541",
    lines: [
      "H No. 71/G, Gate No. 12, Jyoti Nagar,",
      "Jalandhar–1, PO: Jalandhar City,",
      "District: Jalandhar, Punjab – 144001",
    ],
    tint: "glass-tint-purple",
    accentDot: "bg-apex-purple",
  },
  {
    key: "us",
    flag: "🇺🇸",
    label: "United States Office",
    legalName: "APEX Innovation Solutions LLC",
    idLabel: "Utah Entity No.",
    idValue: "14712993-0160",
    lines: [
      "7533 S Center View Ct, Ste 210,",
      "West Jordan, UT 84084,",
      "United States",
    ],
    tint: "glass-tint-blue",
    accentDot: "bg-apex-blue",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="section-pad relative">
      <div className="container-apex">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">
              <span className="w-6 h-px bg-apex-cyan" /> About us
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
              About <span className="text-apex-orange">APEX Innovation Solutions</span>
            </h2>
            <p className="mt-5 text-white/75 text-lg leading-relaxed">
              <strong className="text-white">APEX Innovation Solutions</strong> is a registered digital marketing and web design agency, founded and led by <strong className="text-white">Mr. Jaswinder Singh</strong>. We operate globally through two registered entities — APEX Innovation Solutions (India) and <strong className="text-white">APEX Innovation Solutions LLC</strong> (Utah, USA).
            </p>
            <p className="mt-4 text-white/70 leading-relaxed">
              We help businesses grow through high-converting websites, SEO, AEO &amp; GEO (AI search optimization), landing pages, branding, and performance marketing — serving clients across the United States, India, and worldwide.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <a href="tel:+917719779448" data-testid="about-phone" className="glass-tint-orange glass-hover p-4 flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-apex-orange mt-0.5" strokeWidth={2} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Call</div>
                  <div className="text-sm font-semibold text-white">+91 77197 79448</div>
                </div>
              </a>
              <a href="mailto:info@apexinnovationsolutions.com" data-testid="about-email" className="glass-tint-cyan glass-hover p-4 flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-apex-cyan mt-0.5" strokeWidth={2} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Email</div>
                  <div className="text-sm font-semibold text-white break-all">info@apexinnovationsolutions.com</div>
                </div>
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="glass glass-inner-glow p-6 md:p-8"
            >
              <div className="flex items-center gap-3 pb-5 mb-5 border-b border-white/10">
                <span className="w-11 h-11 rounded-2xl bg-apex-cyan/10 border border-apex-cyan/25 flex items-center justify-center">
                  <BadgeCheck className="w-5 h-5 text-apex-cyan" strokeWidth={1.75} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-apex-cyan font-bold">Verified business</div>
                  <div className="font-heading font-bold text-white text-lg">Two registered entities, one team</div>
                </div>
              </div>

              {/* Founder */}
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <span className="w-10 h-10 rounded-full bg-apex-orange/15 border border-apex-orange/30 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-apex-orange" strokeWidth={2} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Founder &amp; Authorized Signatory</div>
                  <div className="text-white font-heading font-bold text-base" data-testid="about-founder">Mr. Jaswinder Singh</div>
                </div>
              </div>

              {/* Offices — 3 total (2 India, 1 US) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {OFFICES.map((o) => (
                  <div key={o.key} className={`${o.tint} p-5 relative overflow-hidden`} data-testid={`about-office-${o.key}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg leading-none" aria-hidden>{o.flag}</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{o.label}</span>
                    </div>
                    <div className="text-white font-heading font-bold text-sm leading-tight">{o.legalName}</div>
                    <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50">
                      <ShieldCheck className="w-3 h-3" /> {o.idLabel}
                    </div>
                    <div className="text-white/85 font-mono text-xs mt-0.5 break-all">{o.idValue}</div>
                    <div className="mt-3 flex items-start gap-1.5 text-xs text-white/70 leading-relaxed">
                      <MapPin className="w-3 h-3 mt-0.5 text-apex-pink shrink-0" strokeWidth={2} />
                      <address className="not-italic">
                        {o.lines.map((l, idx) => (
                          <span key={idx} className="block">{l}</span>
                        ))}
                      </address>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/55 leading-relaxed">
                All engagements are subject to a written proposal signed by <strong className="text-white/80">Mr. Jaswinder Singh</strong> on behalf of the relevant registered entity above.
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
